import { Controller } from "@hotwired/stimulus"

// Lightweight YouTube embed: shows a high-res thumbnail (loaded directly
// from i.ytimg.com so it stays sharp regardless of iframe size or visibility),
// swaps in the real iframe on click. Avoids loading ~600KB of YouTube player
// JS until the user actually wants to watch.
//
// Usage:
//   <div data-controller="lite-youtube"
//        data-lite-youtube-id-value="EBOmMG6Qe4w"
//        data-lite-youtube-title-value="Getting Started with Primeta">
//   </div>
export default class extends Controller {
  static values = {
    id:    String,           // YouTube video ID
    title: { type: String, default: "Play video" }
  }

  connect() {
    if (this._activated) return
    this.element.classList.add("lite-youtube")
    this.element.style.position = "relative"
    this.element.style.width = "100%"
    this.element.style.paddingBottom = "56.25%"
    this.element.style.cursor = "pointer"
    this.element.setAttribute("role", "button")
    this.element.setAttribute("aria-label", `Play: ${this.titleValue}`)
    this.element.tabIndex = 0
    this._renderThumbnail()
    this.element.addEventListener("click", this._activate)
    this.element.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    this.element.removeEventListener("click", this._activate)
    this.element.removeEventListener("keydown", this._onKey)
  }

  _renderThumbnail() {
    const id = this.idValue
    // maxresdefault is 1280×720 — sharpest available. Falls back to
    // hqdefault.jpg via onerror if the video doesn't have a maxres version
    // (older or low-res uploads).
    const html = `
      <img src="https://i.ytimg.com/vi/${id}/maxresdefault.jpg"
           alt="${this._escape(this.titleValue)} — click to play"
           loading="lazy"
           style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border:1px solid var(--color-border-lt);"
           onerror="this.onerror=null;this.src='https://i.ytimg.com/vi/${id}/hqdefault.jpg'">
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.06);transition:background 0.2s;" class="lite-youtube-overlay">
        <div style="width:64px;height:64px;border-radius:50%;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" style="width:28px;height:28px;margin-left:3px;">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    `
    this.element.innerHTML = html
  }

  _activate = () => {
    if (this._activated) return
    this._activated = true
    const id = this.idValue
    const iframe = document.createElement("iframe")
    iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1`
    iframe.title = this.titleValue
    iframe.setAttribute("frameborder", "0")
    iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture")
    iframe.setAttribute("allowfullscreen", "")
    iframe.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:1px solid var(--color-border-lt);"
    this.element.innerHTML = ""
    this.element.appendChild(iframe)
    this.element.style.cursor = "default"
    this.element.removeAttribute("role")
    this.element.removeAttribute("tabindex")
  }

  _onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this._activate()
    }
  }

  _escape(s) {
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
